// components/ventas/ResumenYPago.jsx
import React, { useState, useEffect, useRef } from 'react';
import { App, Button, Spin, Form, Input, Tooltip } from 'antd';
import {
  UserOutlined, CheckCircleOutlined, IdcardOutlined,
  ClockCircleOutlined, CreditCardOutlined,
  QrcodeOutlined, GiftOutlined, ArrowLeftOutlined,
  WarningOutlined, TagOutlined, MailOutlined, DollarOutlined,
  ReloadOutlined, LockOutlined, ExclamationCircleFilled, CloseCircleFilled
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import VentasPresenciales from '../../../../service/VentaPresencial';
import CodigosManuales from '../../../../service/CodigosManuales';
import { useNotify } from '../../../../providers/NotificationProvider';
import ModalQRPagoV2 from './ModalQRPagoV2';

const getCategoriaEdad = (edad) => {
  if (edad === null || edad === undefined) return { texto: 'Sin edad' };
  if (edad < 18)  return { texto: 'Menor' };
  if (edad >= 60) return { texto: 'Adulto Mayor', purple: true };
  return           { texto: 'Adulto' };
};

const METODOS = [
  { key: 'efectivo', label: 'Efectivo',  icon: <DollarOutlined />,     desc: 'Sin voucher' },
  { key: 'debito',   label: 'Débito',    icon: <CreditCardOutlined />, desc: 'Máquina POS' },
  { key: 'credito',  label: 'Crédito',   icon: <CreditCardOutlined />, desc: 'Máquina POS' },
  { key: 'webpay',   label: 'Webpay QR', icon: <QrcodeOutlined />,     desc: 'En su teléfono' },
];
const NOMBRE_METODO = { efectivo: 'en efectivo', debito: 'con débito', credito: 'con crédito' };

const MostrarHorarios = ({ horarios }) => {
  if (!horarios) return null;
  const lista = Array.isArray(horarios) ? horarios : [horarios];
  return (
    <div className="space-y-1">
      {lista.map((h, i) => {
        const dias = h.bloques
          ? [...new Set(h.bloques.flatMap(b => b.dias))]
          : [h.dia, ...(h.dias || [])].filter(Boolean);
        const tiempo = h.bloques
          ? h.bloques.map(b => `${b.inicio}–${b.fin}`).join(' / ')
          : `${h.inicio}–${h.fin}`;
        return (
          <div key={h._id || i} className="flex items-center gap-2 flex-wrap">
            {dias.map((d, j) => (
              <span key={j} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5
                rounded-full capitalize">{d}</span>
            ))}
            <span className="text-xs font-semibold text-slate-700 flex items-center gap-1">
              <ClockCircleOutlined style={{ fontSize: 10 }} /> {tiempo}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const Tarjeta = ({ titulo, children, className = '' }) => (
  <div className={`bg-white border border-slate-200 rounded-xl p-4 ${className}`}>
    {titulo && (
      <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">{titulo}</div>
    )}
    {children}
  </div>
);

const Aviso = ({ tono = 'azul', icon, children }) => {
  const tonos = {
    azul:     'bg-blue-50 border-blue-200 text-blue-800',
    amarillo: 'bg-[#fffbea] border-[#fde68a] text-[#5c4300]',
  };
  return (
    <div className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-xs ${tonos[tono]}`}>
      {icon && <span className="mt-0.5 flex-shrink-0">{icon}</span>}
      <div>{children}</div>
    </div>
  );
};

/**
 * @param {object} props
 * @param {(estado: 'libre'|'pendiente'|'rechazada'|'no_cobrado'|'revision'|'completada') => void} [props.onEstadoPago]
 *        Avisa al wizard si hay un cobro Webpay en curso (para no cancelar ni salir a mitad).
 * @param {() => void} [props.onReiniciar] Empezar una venta nueva (tras rechazo o revisión).
 */
export default function ResumenYPago({ ventaData, updateVentaData, prevStep, onComplete, onEstadoPago, onReiniciar }) {
  const notify = useNotify();
  const { modal } = App.useApp();
  const [loading, setLoading]                   = useState(false);
  const enProcesoRef                            = useRef(false); // guard anti doble-submit
  const [calculandoPrecio, setCalculandoPrecio] = useState(true);
  const [intentoCalculo, setIntentoCalculo]     = useState(0);
  const [planDetalle, setPlanDetalle]           = useState(null);
  const [planesDetalles, setPlanesDetalles]     = useState([]);
  const [fallosCalculo, setFallosCalculo]       = useState([]);
  // Sin método preseleccionado: el vendedor elige explícitamente cómo pagó el cliente.
  const [metodoPago, setMetodoPago]             = useState(null); // 'efectivo'|'debito'|'credito'|'webpay'
  const [formPOS]                               = Form.useForm();
  const [modalQRv2Open, setModalQRv2Open]       = useState(false);
  // Estado del cobro Webpay: mientras haya un QR vivo no se permite cobrar por
  // otro medio (el cliente podría pagar los dos).
  const [estadoWebpay, setEstadoWebpay]         = useState('libre');

  const formatearPrecio = (p) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(p || 0);

  const esMult = (ventaData.items?.length || 0) > 1;

  const getVentaIds = () =>
    ventaData.items?.length > 0 ? ventaData.items.map(i => i.ventaId) : [ventaData.ventaId];

  const cambiarEstadoWebpay = (estado) => {
    setEstadoWebpay(estado);
    onEstadoPago?.(estado);
  };

  // ── Calcular precios ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!ventaData.planNombre || !ventaData.inscrito) {
      notify.warning('Faltan datos para calcular el precio');
      setCalculandoPrecio(false);
      return;
    }
    const calcularTodos = async () => {
      setCalculandoPrecio(true);
      try {
        const ventaIds = ventaData.items?.length > 0
          ? ventaData.items.map(i => ({ ventaId: i.ventaId, tallerNombre: i.taller?.nombre || '' }))
          : [{ ventaId: ventaData.ventaId, tallerNombre: ventaData.taller?.nombre || '' }];
        const resultados = [];
        const fallidos = [];
        for (const { ventaId, tallerNombre } of ventaIds) {
          // El código manual va al backend para que quede aplicado en el COBRO,
          // no solo mostrado en pantalla (acumula sobre el precio vecino).
          try {
            const res = await VentasPresenciales.CalcularPrecio(
              ventaId, ventaData.planNombre, ventaData.codigoDescuentoManual || null
            );
            if (res?.success) resultados.push({ ventaId, tallerNombre, detalle: res.data });
            else fallidos.push({ tallerNombre, mensaje: res?.message || res?.error });
          } catch (e) {
            fallidos.push({ tallerNombre, mensaje: e?.message });
          }
        }
        // Antes un taller que fallaba se omitía en silencio: el total salía más bajo
        // y se cobraba menos de lo que el backend esperaba. Ahora bloquea el pago.
        setFallosCalculo(fallidos);
        setPlanesDetalles(resultados);
        if (resultados.length > 0) {
          setPlanDetalle(resultados[0].detalle);
          updateVentaData({ planDetalle: resultados[0].detalle });
        }
      } finally {
        setCalculandoPrecio(false);
      }
    };
    calcularTodos();
    // codigoDescuentoManual en deps: si el vendedor agrega/quita el código hay
    // que recalcular el precio en el backend, o se cobra el monto anterior.
  }, [ventaData.ventaId, ventaData.planNombre, ventaData.inscrito, ventaData.codigoDescuentoManual, intentoCalculo]);

  // ── Datos derivados ────────────────────────────────────────────────────────
  const inscrito     = ventaData.inscrito || {};
  const edad         = Number(inscrito.edad) || (inscrito.fechaNacimiento
    ? dayjs().diff(dayjs(inscrito.fechaNacimiento), 'year') : null);
  const categoria    = getCategoriaEdad(edad);
  const esVecino     = ventaData.esVecino;

  const esGratuito   = planDetalle?.esGratuito === true ||
                       ventaData.planNombre === 'gratuito' ||
                       planDetalle?.montoFinal === 0;

  // ── Desglose: el BACKEND es la autoridad del precio ────────────────────────
  // `montoFinal` ya viene con el código manual descontado (acumulado sobre el
  // precio vecino / adulto mayor / particular). Todas las cifras del desglose
  // salen de la respuesta de calcular-precio.
  const descuentos            = planDetalle?.descuentos || {};
  const mejorDescuento        = descuentos.mejorDescuentoAplicado || 'ninguno';
  const sumDet                = (fn) => planesDetalles.reduce((s, p) => s + (fn(p.detalle) || 0), 0);
  const montoDisplay          = sumDet(d => d?.montoFinal ?? d?.valorFinal);
  const totalDescuentoDisplay = sumDet(d => d?.descuentos?.totalDescuento);
  const montoDescuentoManual  = sumDet(d => d?.descuentos?.descuentoManual);
  // Ahorro del tramo (vecino / adulto mayor / automático), sin el código.
  const descuentoTramo        = Math.max(0, totalDescuentoDisplay - montoDescuentoManual);
  const valorOriginalDisplay  = sumDet(d => d?.valorOriginal ?? d?.valorBase);
  const matriculaDisplay      = sumDet(d => d?.matricula);
  // Matrícula del recinto: el backend la suma si el producto la exige y el inscrito
  // no tiene una vigente; si ya la tiene, se avisa al vendedor y no se cobra.
  const infoMatricula         = planesDetalles.map(p => p.detalle?.infoMatricula).find(Boolean);
  const TRAMO_MATRICULA       = { particular: 'no vecino', vecino: 'vecino', adultoMayor: 'adulto mayor', funcionario: 'funcionario' };
  const descuentoManual       = ventaData.descuentoManual;

  const hayQRVivo       = estadoWebpay === 'pendiente';
  const cobroCerrado    = ['rechazada', 'no_cobrado', 'revision', 'completada'].includes(estadoWebpay);
  const posBloqueado    = hayQRVivo || cobroCerrado;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const registrarUsoCodigoManual = async () => {
    if (ventaData.codigoDescuentoManual) {
      try { await CodigosManuales.IncrementarUso(ventaData.codigoDescuentoManual); }
      catch { /* no bloquear la venta si falla el contador */ }
    }
  };

  const handleConfirmarGratuito = async () => {
    if (enProcesoRef.current) return; // ⛔ evita doble-submit
    enProcesoRef.current = true;
    try {
      setLoading(true);
      // Multi-ítem: recolectar resultados por venta (no abortar al primer fallo,
      // que dejaría unas confirmadas y otras no sin poder distinguirlas).
      const ids = getVentaIds();
      const ok = [], fallidos = [];
      for (const vid of ids) {
        try {
          const res = await VentasPresenciales.ConfirmarInscripcionGratuita(vid);
          if (res.success) ok.push(vid); else fallidos.push(vid);
        } catch { fallidos.push(vid); }
      }
      await registrarUsoCodigoManual();
      if (fallidos.length === 0) {
        notify.success(`${ids.length > 1 ? ids.length + ' inscripciones confirmadas' : 'Inscripción confirmada'}!`);
        onComplete({ totalCobrado: 0 });
      } else if (ok.length > 0) {
        notify.warning(`${ok.length} de ${ids.length} inscripciones confirmadas. ${fallidos.length} fallaron; reintenta solo esas (el backend es idempotente, no duplica las ya confirmadas).`);
      } else {
        notify.error('No se pudo confirmar ninguna inscripción. Reintenta.');
      }
    } finally {
      setLoading(false);
      enProcesoRef.current = false;
    }
  };

  const handlePagarPOS = async (values) => {
    if (posBloqueado) return; // hay un cobro Webpay vivo o ya resuelto
    if (enProcesoRef.current) return; // ⛔ evita doble-submit
    enProcesoRef.current = true;
    try {
      setLoading(true);
      // Efectivo: no se envía voucher (no pasa por la máquina POS); el backend lo
      // registra como metodoPago.tipo='efectivo'. Débito/Crédito envían el voucher.
      const payload = metodoPago === 'efectivo'
        ? { pagoExitoso: true, tipoPago: 'efectivo' }
        : {
            pagoExitoso: true,
            tipoPago: metodoPago, // 'debito' | 'credito'
            detallesPOS: {
              numeroVoucher:         values.numeroVoucher,
              codigoAutorizacion:    values.codigoAutorizacion,
              ultimosDigitosTarjeta: values.ultimosDigitos
            }
          };
      // Multi-ítem: recolectar parciales (idempotencia backend hace seguro reintentar).
      const ids = getVentaIds();
      const ok = [], fallidos = [];
      for (const vid of ids) {
        try {
          const res = await VentasPresenciales.ProcesarPagoPOS(vid, payload);
          if (res.success && res.data?.ventaCompletada) ok.push(vid); else fallidos.push(vid);
        } catch { fallidos.push(vid); }
      }
      await registrarUsoCodigoManual();
      if (fallidos.length === 0) {
        notify.success(`${ids.length > 1 ? ids.length + ' ventas completadas' : 'Venta completada'}!`);
        onComplete({ totalCobrado: montoDisplay });
      } else if (ok.length > 0) {
        notify.warning(`${ok.length} de ${ids.length} ventas completadas. ${fallidos.length} fallaron; reintenta solo esas (el backend es idempotente, no re-cobra las ya pagadas).`);
      } else {
        notify.error('Ninguna venta pudo completarse. Reintenta.');
      }
    } finally {
      setLoading(false);
      enProcesoRef.current = false;
    }
  };

  // Registrar un pago emite la boleta y el correo: se confirma antes.
  const confirmarCobro = (values = {}) => {
    modal.confirm({
      title: `¿Registrar pago ${NOMBRE_METODO[metodoPago]}?`,
      icon: <ExclamationCircleFilled style={{ color: '#0047bb' }} />,
      content: (
        <div className="space-y-2 text-sm text-slate-600">
          <div className="text-2xl font-bold text-slate-800">{formatearPrecio(montoDisplay)}</div>
          {metodoPago !== 'efectivo' && (
            <div className="text-xs font-mono text-slate-500">
              Voucher {values.numeroVoucher} · Aut. {values.codigoAutorizacion} · **** {values.ultimosDigitos}
            </div>
          )}
          <p>Confirma que el cliente ya pagó. Se emitirá la boleta y se enviará el correo; no se puede deshacer desde aquí.</p>
        </div>
      ),
      okText: 'Sí, registrar pago',
      cancelText: 'Volver',
      onOk: () => handlePagarPOS(values),
    });
  };

  const elegirMetodo = (key) => {
    if (posBloqueado) return;
    if (key === 'webpay' && esMult) return;
    setMetodoPago(key);
  };

  // ── Loading ────────────────────────────────────────────────────────────────
  if (calculandoPrecio) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Spin size="large" />
        <p className="text-slate-500 text-sm mt-4 font-medium">Calculando precio con descuentos...</p>
      </div>
    );
  }

  if ((!planDetalle && !esGratuito) || fallosCalculo.length > 0) {
    return (
      <div className="max-w-xl mx-auto bg-white border border-slate-200 rounded-xl p-6 text-center">
        <CloseCircleFilled style={{ fontSize: 40, color: '#dc2626' }} />
        <h3 className="mt-3 font-bold text-slate-800">No se pudo calcular el precio</h3>
        <p className="text-sm text-slate-500 mt-1">
          No se puede cobrar sin el monto confirmado por el sistema.
        </p>
        {fallosCalculo.length > 0 && (
          <ul className="mt-3 text-left text-xs text-slate-600 bg-slate-50 rounded-lg px-4 py-3 space-y-1">
            {fallosCalculo.map((f, i) => (
              <li key={i}><strong>{f.tallerNombre || 'Taller'}:</strong> {f.mensaje || 'error desconocido'}</li>
            ))}
          </ul>
        )}
        <div className="mt-5 flex justify-center gap-2">
          <Button icon={<ArrowLeftOutlined />} onClick={prevStep}>Volver</Button>
          <Button type="primary" icon={<ReloadOutlined />} onClick={() => setIntentoCalculo(n => n + 1)}>
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  const botonCobro = { background: '#feda2c', borderColor: '#feda2c', color: '#00266b' };

  const BarraTotal = ({ children }) => (
    <div className="bg-[#00266b] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <div className="text-xs text-[#b9c8e8] font-medium">Total a cobrar</div>
        <div className="text-2xl font-bold text-white">{formatearPrecio(montoDisplay)}</div>
      </div>
      {children}
    </div>
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Spin spinning={loading}>
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

          {/* ── COLUMNA IZQ: Resumen ──────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-4">

            <Tarjeta titulo="Cliente">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-[#fffbea] border border-[#fde68a] rounded-full flex items-center
                  justify-center flex-shrink-0">
                  <UserOutlined className="text-[#5c4300]" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-800 text-sm truncate">{inscrito.nombre}</div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="text-xs text-slate-400">{inscrito.rut}</span>
                    {edad !== null && (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                        categoria.purple ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {edad} años · {categoria.texto}
                      </span>
                    )}
                    {esVecino && (
                      <span className="text-xs bg-[#feda2c] text-[#5c4300]
                        font-semibold px-1.5 py-0.5 rounded flex items-center gap-1">
                        <IdcardOutlined style={{ fontSize: 10 }} /> Vecino
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Tarjeta>

            <Tarjeta titulo={esMult ? `${ventaData.items.length} talleres` : 'Taller'}>
              {esMult ? (
                <div className="space-y-3">
                  {ventaData.items.map((item, idx) => {
                    const det = planesDetalles.find(p => p.ventaId === item.ventaId);
                    const precio = det ? (det.detalle?.montoFinal ?? det.detalle?.valorFinal ?? 0) : null;
                    return (
                      <div key={item.ventaId || idx} className="border border-slate-100 rounded-lg p-3">
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="font-semibold text-slate-700 text-sm">
                            {idx + 1}. {item.taller?.nombre}
                          </div>
                          {precio !== null && (
                            <span className="text-sm font-bold text-slate-800 whitespace-nowrap">
                              {formatearPrecio(precio)}
                            </span>
                          )}
                        </div>
                        <MostrarHorarios horarios={item.horarios || item.horario} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div>
                  <div className="font-semibold text-slate-800 text-sm mb-2">{ventaData.taller?.nombre}</div>
                  <MostrarHorarios horarios={ventaData.horarios || ventaData.horario} />
                </div>
              )}
            </Tarjeta>

            <Tarjeta titulo="Plan">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-700 capitalize text-sm">{ventaData.planNombre}</span>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                  esGratuito || mejorDescuento === 'vecino'
                    ? 'bg-blue-100 text-blue-700'
                    : mejorDescuento === 'adultoMayor'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-slate-100 text-slate-600'
                }`}>
                  {esGratuito ? 'Gratuito'
                    : mejorDescuento === 'vecino' ? 'Precio Vecino'
                    : mejorDescuento === 'adultoMayor' ? 'Adulto Mayor'
                    : mejorDescuento === 'automatico' ? 'Dcto. automático'
                    : 'Particular'}
                </span>
              </div>

              {ventaData.codigoDescuentoManual && descuentoManual && (
                <div className="mt-2 flex items-center gap-2 text-xs text-[#5c4300]
                  bg-[#fffbea] border border-[#fde68a] rounded-lg px-3 py-2">
                  <TagOutlined />
                  <span className="font-bold">{ventaData.codigoDescuentoManual}</span>
                  <span>— {descuentoManual.nombre}</span>
                </div>
              )}

              {!esGratuito && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-1">
                  {valorOriginalDisplay > 0 && totalDescuentoDisplay > 0 && (
                    <div className="flex justify-between text-xs text-slate-400">
                      <span>Precio original</span>
                      <span className="line-through">{formatearPrecio(valorOriginalDisplay)}</span>
                    </div>
                  )}
                  {descuentoTramo > 0 && (
                    <div className="flex justify-between text-xs text-blue-600 font-medium">
                      <span>
                        {mejorDescuento === 'vecino'      ? 'Dcto. Tarjeta Vecino'
                          : mejorDescuento === 'adultoMayor' ? 'Dcto. Adulto Mayor'
                          : 'Dcto. automático'}
                      </span>
                      <span>−{formatearPrecio(descuentoTramo)}</span>
                    </div>
                  )}
                  {montoDescuentoManual > 0 && (
                    <div className="flex justify-between text-xs text-[#8a6500] font-medium">
                      <span>
                        Dcto. código
                        {descuentoManual?.tipoDescuento === 'porcentaje' ? ` (${descuentoManual.valorDescuento}%)` : ''}
                      </span>
                      <span>−{formatearPrecio(montoDescuentoManual)}</span>
                    </div>
                  )}
                  {matriculaDisplay > 0 && (
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>
                        Matrícula
                        {infoMatricula && !infoMatricula.tieneMatricula
                          ? ` ${infoMatricula.recintoNombre || ''} (no tiene vigente${infoMatricula.tipoPrecio ? ` · ${TRAMO_MATRICULA[infoMatricula.tipoPrecio] || infoMatricula.tipoPrecio}` : ''})`
                          : ''}
                      </span>
                      <span>+{formatearPrecio(matriculaDisplay)}</span>
                    </div>
                  )}
                  {infoMatricula?.tieneMatricula && (
                    <div className="text-xs text-green-700">
                      ✓ Ya tiene matrícula vigente{infoMatricula.venceEl ? ` hasta el ${new Date(infoMatricula.venceEl).toLocaleDateString('es-CL')}` : ''}: no se cobra.
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold text-slate-800 pt-1">
                    <span>Total{esMult ? ` (${ventaData.items.length} talleres)` : ''}</span>
                    <span>{formatearPrecio(montoDisplay)}</span>
                  </div>
                  {totalDescuentoDisplay > 0 && (
                    <div className="text-right text-xs font-medium text-blue-600">
                      Ahorro total: {formatearPrecio(totalDescuentoDisplay)}
                    </div>
                  )}
                </div>
              )}
            </Tarjeta>
          </div>

          {/* ── COLUMNA DER: Pago ─────────────────────────────────────── */}
          <div className="lg:col-span-3">
            {esGratuito ? (
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center h-full
                flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-[#fffbea] border border-[#fde68a] rounded-full flex items-center
                  justify-center mb-4">
                  <GiftOutlined className="text-[#5c4300] text-2xl" />
                </div>
                <h3 className="font-bold text-slate-800 text-xl mb-2">
                  {esMult ? `${ventaData.items.length} talleres gratuitos` : 'Taller gratuito'}
                </h3>
                <p className="text-slate-400 text-sm mb-8 max-w-xs">
                  No se requiere pago. Confirma la inscripción para completar el proceso.
                </p>
                <Button
                  type="primary" size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleConfirmarGratuito}
                  loading={loading} block
                  className="h-12 font-semibold text-base"
                  style={botonCobro}
                >
                  Confirmar inscripción gratuita
                </Button>
              </div>
            ) : cobroCerrado && estadoWebpay !== 'completada' ? (
              /* ── Webpay terminó en rechazo o revisión: no se cobra más aquí ── */
              <div className="bg-white border border-slate-200 rounded-xl p-8 text-center h-full
                flex flex-col items-center justify-center">
                {estadoWebpay === 'revision' ? (
                  <>
                    <ExclamationCircleFilled style={{ fontSize: 48, color: '#d97706' }} />
                    <h3 className="mt-3 font-bold text-slate-800 text-lg">Pago recibido, en revisión</h3>
                    <p className="text-sm text-slate-500 mt-1 max-w-sm">
                      Transbank aprobó el cobro pero la inscripción no se completó sola.
                      <strong> No vuelvas a cobrar:</strong> el caso queda en Operaciones.
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-2">
                      <Link to="/admin/operaciones"><Button>Ir a Operaciones</Button></Link>
                      {onReiniciar && <Button type="primary" onClick={onReiniciar}>Iniciar otra venta</Button>}
                    </div>
                  </>
                ) : (
                  <>
                    <CloseCircleFilled style={{ fontSize: 48, color: '#dc2626' }} />
                    <h3 className="mt-3 font-bold text-slate-800 text-lg">
                      {estadoWebpay === 'no_cobrado' ? 'El pago Webpay no se realizó' : 'Webpay rechazó el pago'}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1 max-w-sm">
                      {estadoWebpay === 'no_cobrado'
                        ? 'La venta ya no estaba activa, así que no se confirmó el cobro. Para cobrar, inicia una venta nueva.'
                        : 'No se cobró nada. La venta quedó cancelada y los cupos se liberaron; para cobrar por otro medio inicia una venta nueva.'}
                    </p>
                    {onReiniciar && (
                      <Button type="primary" className="mt-6" onClick={onReiniciar}>Iniciar venta nueva</Button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    ¿Cómo paga el cliente?
                  </span>
                  {hayQRVivo && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#5c4300]">
                      <LockOutlined /> QR Webpay activo
                    </span>
                  )}
                </div>

                {/* Selector de método */}
                <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {METODOS.map(m => {
                    const bloqueadoMult = m.key === 'webpay' && esMult;
                    const bloqueado = bloqueadoMult || (posBloqueado && m.key !== 'webpay');
                    const activo = metodoPago === m.key;
                    const boton = (
                      <button
                        type="button"
                        key={m.key}
                        disabled={bloqueado}
                        onClick={() => elegirMetodo(m.key)}
                        aria-pressed={activo}
                        className={`w-full flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all ${
                          bloqueado
                            ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                            : activo
                            ? 'border-[#0047bb] bg-blue-50 shadow-[0_0_0_3px_#feda2c55]'
                            : 'border-slate-200 hover:border-[#0047bb]/40'
                        }`}
                      >
                        <div className={`text-2xl ${activo ? 'text-[#0047bb]' : 'text-slate-400'}`}>{m.icon}</div>
                        <div className={`font-semibold text-sm ${activo ? 'text-[#00266b]' : 'text-slate-700'}`}>{m.label}</div>
                        <div className="text-xs text-slate-400 text-center">{m.desc}</div>
                      </button>
                    );
                    const motivo = bloqueadoMult
                      ? 'Webpay cobra una sola venta: con varios talleres usa efectivo o tarjeta'
                      : bloqueado ? 'Hay un QR Webpay activo: verifícalo o descártalo primero' : null;
                    return motivo
                      ? <Tooltip key={m.key} title={motivo}><span className="block">{boton}</span></Tooltip>
                      : boton;
                  })}
                </div>

                <div className="px-4 pb-4">
                  {!metodoPago && (
                    <p className="text-center text-sm text-slate-400 py-6">
                      Elige el medio de pago para continuar.
                    </p>
                  )}

                  {metodoPago === 'efectivo' && (
                    <div className="space-y-3">
                      <Aviso tono="amarillo" icon={<DollarOutlined />}>
                        <p className="font-semibold text-sm mb-0.5">Cobro en efectivo</p>
                        No pasa por la máquina POS ni lleva voucher. Igual se emite la boleta al cliente.
                      </Aviso>
                      <Aviso icon={<MailOutlined />}>
                        Al confirmar se enviará un correo con los datos de la compra al cliente.
                      </Aviso>
                      <BarraTotal>
                        <Button
                          onClick={() => confirmarCobro({})}
                          loading={loading} disabled={posBloqueado}
                          icon={<CheckCircleOutlined />} size="large"
                          className="h-12 px-6 font-semibold" style={botonCobro}
                        >
                          Registrar pago en efectivo{esMult ? ` (${ventaData.items.length})` : ''}
                        </Button>
                      </BarraTotal>
                    </div>
                  )}

                  {(metodoPago === 'debito' || metodoPago === 'credito') && (
                    <div className="space-y-3">
                      <Aviso icon={<CreditCardOutlined />}>
                        <p className="font-semibold text-sm mb-0.5">Ingresa los datos del voucher POS</p>
                        Están en el comprobante impreso de {metodoPago === 'debito' ? 'débito' : 'crédito'}.
                        Al confirmar se enviará un correo con los datos de la compra al cliente.
                      </Aviso>

                      <Form form={formPOS} onFinish={confirmarCobro} layout="vertical" requiredMark={false}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3">
                          <Form.Item
                            name="numeroVoucher" label="N° de voucher"
                            rules={[{ required: true, message: 'Ingresa el número de voucher' }]}
                          >
                            <Input placeholder="123456" className="font-mono" />
                          </Form.Item>
                          <Form.Item
                            name="codigoAutorizacion" label="Código de autorización"
                            rules={[{ required: true, message: 'Ingresa el código' }]}
                          >
                            <Input placeholder="ABC123" className="font-mono" />
                          </Form.Item>
                          <Form.Item
                            name="ultimosDigitos" label="Últimos 4 dígitos"
                            rules={[
                              { required: true, message: 'Ingresa los 4 dígitos' },
                              { pattern: /^\d{4}$/, message: 'Deben ser 4 dígitos' }
                            ]}
                          >
                            <Input placeholder="1234" maxLength={4} inputMode="numeric" className="font-mono" />
                          </Form.Item>
                        </div>
                        <BarraTotal>
                          <Button
                            htmlType="submit" loading={loading} disabled={posBloqueado}
                            icon={<CheckCircleOutlined />} size="large"
                            className="h-12 px-6 font-semibold" style={botonCobro}
                          >
                            Confirmar pago{esMult ? ` (${ventaData.items.length})` : ''}
                          </Button>
                        </BarraTotal>
                      </Form>
                    </div>
                  )}

                  {metodoPago === 'webpay' && (
                    <div className="space-y-3">
                      {hayQRVivo ? (
                        <Aviso tono="amarillo" icon={<WarningOutlined />}>
                          <p className="font-semibold text-sm mb-0.5">Hay un QR esperando el pago</p>
                          El cliente todavía puede pagarlo. Ábrelo para verificar el pago, o descártalo
                          si el cliente no pagará por Webpay.
                        </Aviso>
                      ) : (
                        <Aviso icon={<QrcodeOutlined />}>
                          Genera el código QR y muéstraselo al cliente para que pague desde su teléfono.
                        </Aviso>
                      )}
                      <BarraTotal>
                        <Button
                          type="primary" onClick={() => setModalQRv2Open(true)} loading={loading}
                          icon={<QrcodeOutlined />} size="large" className="h-12 px-6 font-semibold"
                          style={botonCobro}
                        >
                          {hayQRVivo ? 'Abrir QR' : 'Generar QR'}
                        </Button>
                      </BarraTotal>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div>
          <Tooltip title={estadoWebpay !== 'libre' ? 'Hay un cobro Webpay en curso' : null}>
            <Button icon={<ArrowLeftOutlined />} onClick={prevStep} disabled={estadoWebpay !== 'libre'}>
              Anterior
            </Button>
          </Tooltip>
        </div>
      </div>

      {/* Webpay QR REAL (Transbank) vía v2. Solo ventas de un taller: el backend
          cobra una venta presencial por transacción. */}
      {!esMult && (
        <ModalQRPagoV2
          open={modalQRv2Open}
          onClose={() => setModalQRv2Open(false)}
          ventaPresencialId={ventaData.ventaId}
          codigoVenta={ventaData.codigoVenta}
          monto={montoDisplay}
          returnUrl={`${window.location.origin}/v2/venta-presencial/return`}
          onEstadoPago={cambiarEstadoWebpay}
          onCompletado={async (resultado) => {
            await registrarUsoCodigoManual();
            notify.success('¡Pago confirmado! Venta completada');
            setModalQRv2Open(false);
            onComplete({ totalCobrado: resultado?.monto ?? montoDisplay });
          }}
        />
      )}
    </Spin>
  );
}
